import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trackFilterChange, trackWalletInteraction } from '../../utils/analytics';
import './PositionFilters.css';

/**
 * Position Filters Component
 * Filters for Hyperliquid positions: min size, side, symbols, user search
 * Following VolumeSlider.jsx pattern with grey-black theme
 */
function PositionFilters({ filters, setMinPositionSize, setSide, setSymbols, setUserSearch, hasActiveFilters }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [symbolInput, setSymbolInput] = useState('');
  const [selectedSymbols, setSelectedSymbolsLocal] = useState(filters.symbols || []);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Track initial values for analytics
  const sizeStartValue = useRef(filters.minPositionSize);

  // Popular Hyperliquid symbols
  const popularSymbols = ['BTC', 'ETH', 'SOL', 'ARB', 'MATIC', 'AVAX', 'DOGE', 'XRP', 'LINK', 'UNI'];

  // Track position size changes on slider release
  const handleSizeChangeEnd = () => {
    if (filters.minPositionSize !== sizeStartValue.current) {
      const newValue = filters.minPositionSize;
      const prevValue = sizeStartValue.current;
      sizeStartValue.current = newValue;
      setTimeout(() => {
        trackFilterChange('hyperliquid_min_size', newValue, {
          page: '/hyperliquid',
          previous_value: prevValue
        });
      }, 0);
    }
  };

  // Track side filter changes
  const handleSideChange = (side) => {
    trackFilterChange('hyperliquid_side', side, { page: '/hyperliquid' });
    setSide(side);
  };

  // Sync local state with filters prop
  useEffect(() => {
    setSelectedSymbolsLocal(filters.symbols || []);
  }, [filters.symbols]);

  // Format value for display
  const formatValue = (value) => {
    if (value >= 1e6) return '$' + (value / 1e6).toFixed(1) + 'M';
    if (value >= 1e3) return '$' + (value / 1e3).toFixed(0) + 'K';
    return '$' + value;
  };

  // Handle symbol input change
  const handleSymbolInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setSymbolInput(value);

    if (value.length > 0) {
      const filtered = popularSymbols.filter(symbol =>
        symbol.includes(value) && !selectedSymbols.includes(symbol)
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (symbol) => {
    if (!selectedSymbols.includes(symbol)) {
      const newSymbols = [...selectedSymbols, symbol];
      setSelectedSymbolsLocal(newSymbols);
      setSymbols(newSymbols);
    }
    setSymbolInput('');
    setShowSuggestions(false);
  };

  // Handle remove symbol
  const handleRemoveSymbol = (symbolToRemove) => {
    const newSymbols = selectedSymbols.filter(s => s !== symbolToRemove);
    setSelectedSymbolsLocal(newSymbols);
    setSymbols(newSymbols);
  };

  // Handle Enter key in symbol input
  const handleSymbolInputKeyDown = (e) => {
    if (e.key === 'Enter' && symbolInput.trim()) {
      const symbol = symbolInput.trim().toUpperCase();
      if (!selectedSymbols.includes(symbol)) {
        const newSymbols = [...selectedSymbols, symbol];
        setSelectedSymbolsLocal(newSymbols);
        setSymbols(newSymbols);
      }
      setSymbolInput('');
      setShowSuggestions(false);
    }
  };

  // Handle Go to Profile button click
  const handleGoToProfile = () => {
    const address = filters.userSearch?.trim();
    if (address && address.length > 0) {
      trackWalletInteraction('search_profile', address, { page: '/hyperliquid' });
      // Navigate to wallet profile page
      navigate(`/hyperliquid/wallet/${address}`);
    }
  };

  // Handle Enter key in user search input
  const handleUserSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleGoToProfile();
    }
  };

  return (
    <div className="card bg-main border-0 mb-3">
      <div className="card-header bg-main">
        <h6 className="mb-0">{t('positionFilters.title')}</h6>
      </div>
      <div className="card-body">
        {/* Min Position Size Filter */}
        <div className="mb-3">
          <label className="form-label text-light small">
            {t('positionFilters.minPositionSize')}: <strong>{formatValue(filters.minPositionSize)}</strong>
          </label>
          <input
            type="range"
            className="form-range"
            min="25000"
            max="10000000"
            step="25000"
            value={filters.minPositionSize}
            onChange={(e) => setMinPositionSize(Number(e.target.value))}
            onMouseUp={handleSizeChangeEnd}
            onTouchEnd={handleSizeChangeEnd}
          />
          <div className="d-flex justify-content-between small text-muted">
            <span>$25K</span>
            <span>$10M</span>
          </div>
        </div>

        {/* Side Filter (Long/Short/All) */}
        <div className="mb-3">
          <label className="form-label text-light small d-block">{t('positionFilters.side')}</label>
          <div className="btn-group w-100" role="group">
            <button
              type="button"
              className={`btn btn-sm ${filters.side === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleSideChange('all')}
            >
              {t('positionFilters.all')}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filters.side === 'long' ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={() => handleSideChange('long')}
            >
              {t('hyperliquid.long')}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filters.side === 'short' ? 'btn-danger' : 'btn-outline-secondary'}`}
              onClick={() => handleSideChange('short')}
            >
              {t('hyperliquid.short')}
            </button>
          </div>
        </div>

        {/* Symbol Filter */}
        <div className="mb-3">
          <label className="form-label text-light small">{t('positionFilters.symbolsOptional')}</label>
          <div className="position-relative">
            <input
              type="text"
              className="form-control form-control-sm bg-dark text-light border-secondary"
              placeholder="BTC, ETH, SOL..."
              value={symbolInput}
              onChange={handleSymbolInputChange}
              onKeyDown={handleSymbolInputKeyDown}
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="position-filters-suggestions">
                {suggestions.map(symbol => (
                  <div
                    key={symbol}
                    className="position-filters-suggestion"
                    onClick={() => handleSuggestionClick(symbol)}
                  >
                    {symbol}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Symbols */}
          {selectedSymbols.length > 0 && (
            <div className="selected-symbols-container mt-2">
              {selectedSymbols.map(symbol => (
                <span key={symbol} className="symbol-chip">
                  {symbol}
                  <button
                    className="symbol-chip-remove"
                    onClick={() => handleRemoveSymbol(symbol)}
                    type="button"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <small className="form-text text-muted d-block mt-1">
            {selectedSymbols.length > 0
              ? t('positionFilters.symbolsSelected', { count: selectedSymbols.length })
              : t('positionFilters.leaveEmptyForAll')}
          </small>
        </div>

        {/* User Search */}
        <div>
          <label className="form-label text-light small">{t('positionFilters.searchUserAddress')}</label>
          <div className="input-group input-group-sm">
            <input
              type="text"
              className="form-control bg-dark text-light border-secondary"
              placeholder={t('positionFilters.userAddressPlaceholder')}
              value={filters.userSearch || ''}
              onChange={(e) => setUserSearch(e.target.value)}
              onKeyDown={handleUserSearchKeyDown}
            />
            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={handleGoToProfile}
              disabled={!filters.userSearch || filters.userSearch.trim().length === 0}
              title="Go to wallet profile"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"/>
              </svg>
            </button>
          </div>
          <small className="form-text text-muted d-block mt-1">
            {t('positionFilters.userAddressHint')}
          </small>
        </div>
      </div>
    </div>
  );
}

export default PositionFilters;
